import { ActionResult } from '~/hooks/actions/enums';
import type { ActionHandler } from '~/hooks/actions/types';
import { confirmDialogHandler } from '~/hooks/actions/defaultActions/confirmDialog.ts';
import logger from '~/services/logger';
import { getFormScreenRuntime } from './form-screen-state.ts';

export { confirmDialogHandler as confirmDialogAction };

/**
 * Resolves `@{screen/...}` placeholders used by form button actions.
 *
 * @param inner - Placeholder inner value (e.g. `screen/`, `screen/profileName`).
 * @returns Resolved string value or null when unavailable.
 */
export function resolveFormScreenPlaceholder(inner: string): string | null {
  const runtime = getFormScreenRuntime();
  if (!runtime) return null;

  if (inner === 'screen' || inner === 'screen/') {
    const rawData = runtime.state.getData();
    const normalized =
      runtime.formDataModel?.normalizeValues(rawData) ?? rawData;
    return JSON.stringify(normalized);
  }

  const fieldPrefix = 'screen/';
  if (inner.startsWith(fieldPrefix)) {
    const fieldId = inner.slice(fieldPrefix.length);
    const value = runtime.state.getValue(fieldId);
    return value ?? null;
  }

  return null;
}

/**
 * Placeholder strategy for form screen context inflation.
 */
export const formScreenPlaceholderStrategy = {
  pattern: 'screen',
  resolve: resolveFormScreenPlaceholder,
};

type NavigateToScreenOptions = {
  typeMapping?: string;
};

/**
 * Validates the active form screen before continuing the action chain.
 */
export const validateFormScreenAction: ActionHandler = () => {
  const runtime = getFormScreenRuntime();
  const model = runtime?.formDataModel;

  if (!runtime || !model) {
    logger.info('validateFormScreenAction: no active form screen runtime');
    return ActionResult.Error;
  }

  model.validateFormData(runtime.state.getData());
  return model.validateFormScreenActionFired();
};

/**
 * Navigates to a screen resolved by content type mapping.
 */
export const navigateToScreenAction: ActionHandler<NavigateToScreenOptions> = (
  action,
  ctx
) => {
  const typeMapping = action.options?.typeMapping;

  if (typeof typeMapping !== 'string' || typeMapping.length === 0) {
    logger.info('navigateToScreenAction: missing typeMapping');
    return ActionResult.Error;
  }

  const path = `/_content-type/${encodeURIComponent(typeMapping)}`;

  if (!ctx.navigate) {
    logger.info('navigateToScreenAction: navigate is unavailable in context');
    return ActionResult.Error;
  }

  ctx.navigate(path);
  return ActionResult.Success;
};

type FormScreenActionRegistration = {
  register: (type: string, handler: ActionHandler) => () => void;
};

/**
 * Registers form-screen action handlers on the shared action registry.
 *
 * @param registration - Registry register function.
 * @returns Unregister function for all handlers.
 */
export function registerFormScreenActions({
  register,
}: FormScreenActionRegistration): () => void {
  const unregisterValidate = register('validateForm', validateFormScreenAction);
  const unregisterValidateFormScreen = register(
    'validate_form_screen',
    validateFormScreenAction
  );
  const unregisterNavigate = register(
    'navigateToScreen',
    navigateToScreenAction
  );

  return () => {
    unregisterValidate();
    unregisterValidateFormScreen();
    unregisterNavigate();
  };
}
